/**
 * Terms/Privacy Consent Status Composable
 *
 * /api/auth/me 의 consent({needsTermsAcceptance,...}) + 응답 403 TERMS_NOT_ACCEPTED 를 감지해
 * 전역 상태 관리. needsConsent 이면 동의 모달을 띄우고, 동의 전에는 서비스 사용 불가(하드 차단).
 * useSubscriptionStatus 와 동일 패턴.
 */

import { ref, computed } from 'vue'
import { apiClient } from '@/shared/api/client'

const needsConsent = ref<boolean>(false)
const currentTermsVersion = ref<string | null>(null)
const currentPrivacyVersion = ref<string | null>(null)

// 시행 전 최신본이 게시됐고 아직 동의 안 함 → 차단은 아니고 "닫을 수 있는 안내"만.
const hasUpcoming = ref<boolean>(false)
const latestTermsVersion = ref<string | null>(null)

// 안내를 닫은 기록(기기별). 서버에 두려면 컬럼이 필요해 localStorage 로 둔다 —
// 안내 성격이라 기기당 한 번이면 충분하다. ⚠️ 하드 차단(needsConsent)은 이 값을 보지 않는다.
const DISMISS_KEY = 'legalNoticeDismissed'
function dismissedVersion(): string | null {
  try { return localStorage.getItem(DISMISS_KEY) } catch { return null }
}
export function dismissUpcomingNotice() {
  try { if (latestTermsVersion.value) localStorage.setItem(DISMISS_KEY, latestTermsVersion.value) } catch { /* 사생활 보호 모드 등 */ }
  hasUpcoming.value = false
}
/** 안내 모달을 띄울지 — 시행 전 최신본이 있고, 이 기기에서 아직 닫지 않았을 때만. */
const showUpcomingNotice = computed(
  () => hasUpcoming.value && !needsConsent.value && dismissedVersion() !== latestTermsVersion.value,
)

/** /api/auth/me 의 consent 로 전역 상태 세팅(라우터 가드에서 호출). */
export function setConsent(consent?: {
  needsTermsAcceptance?: boolean
  currentTermsVersion?: string | null
  currentPrivacyVersion?: string | null
  hasUpcomingTerms?: boolean
  latestTermsVersion?: string | null
} | null) {
  if (!consent) return
  needsConsent.value = !!consent.needsTermsAcceptance
  if (consent.currentTermsVersion !== undefined) currentTermsVersion.value = consent.currentTermsVersion ?? null
  if (consent.currentPrivacyVersion !== undefined) currentPrivacyVersion.value = consent.currentPrivacyVersion ?? null
  hasUpcoming.value = !!consent.hasUpcomingTerms
  if (consent.latestTermsVersion !== undefined) latestTermsVersion.value = consent.latestTermsVersion ?? null
}

/** 동의 완료 처리(모달에서 accept 성공 시 호출). */
export function markConsented() {
  needsConsent.value = false
  hasUpcoming.value = false
}

let interceptorRegistered = false

function registerInterceptor() {
  if (interceptorRegistered) return
  interceptorRegistered = true

  // ⚠️ 헤더를 보고 needsConsent를 false로 되돌리지 않는다. revoke 직전에 이미 날아가 있던
  // (대시보드·알림 등) 다른 요청이 revoke 이후 뒤늦게 응답으로 도착하면, 그 응답은 요청 시점
  // 기준의 "그때는 동의된 상태였다"는 오래된(stale) 헤더값을 들고 있어서, 지금 막 뜬 동의 모달을
  // 그 자리에서 닫아버리는 경쟁 상태(race condition)가 생긴다. true로 켜는 것만 헤더/에러로 반응하고,
  // false로 끄는 것은 오직 markConsented()(동의 성공 시 명시적 호출)로만 하도록 제한한다.
  const instance = apiClient.getInstance()
  instance.interceptors.response.use(
    (response) => {
      const accepted = response.headers?.['x-terms-accepted']
      if (accepted != null && String(accepted) === '0') needsConsent.value = true
      return response
    },
    (error) => {
      const data = error?.response?.data
      if (error?.response?.status === 403 && data?.error?.code === 'TERMS_NOT_ACCEPTED') {
        needsConsent.value = true
        if (data.error.currentTermsVersion) currentTermsVersion.value = data.error.currentTermsVersion
        if (data.error.currentPrivacyVersion) currentPrivacyVersion.value = data.error.currentPrivacyVersion
      }
      const accepted = error?.response?.headers?.['x-terms-accepted']
      if (accepted != null && String(accepted) === '0') needsConsent.value = true
      return Promise.reject(error)
    },
  )
}

export function useTermsConsent() {
  registerInterceptor()
  return {
    needsConsent,
    currentTermsVersion,
    currentPrivacyVersion,
    hasUpcoming,
    latestTermsVersion,
    showUpcomingNotice,
    dismissUpcomingNotice,
    setConsent,
    markConsented,
  }
}
